import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, Home, Settings, Bell, HelpCircle, Info, BookOpen, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from './ui/Button';
import { supabase, signOut } from '../lib/supabase';

interface NavigationProps {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isOpen: boolean) => void;
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
}

const navVariants = {
  hidden: { y: -10, opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 25,
      mass: 0.5,
      duration: 0.1
    }
  }
};

const sidebarVariants = {
  hidden: { x: '100%', opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 25,
      mass: 0.5,
      duration: 0.15,
      staggerChildren: 0.02,
      delayChildren: 0.05
    }
  },
  exit: {
    x: '100%',
    opacity: 0,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 25,
      mass: 0.5,
      duration: 0.1,
      staggerChildren: 0.01,
      staggerDirection: -1
    }
  }
};

const itemVariants = {
  hidden: { x: 10, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 25,
      mass: 0.5,
      duration: 0.1
    }
  },
  exit: {
    x: 10,
    opacity: 0,
    transition: {
      duration: 0.05
    }
  }
};

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.1
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.1
    }
  }
};

const logoVariants = {
  hover: {
    scale: 1.05,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 25,
      duration: 0.1
    }
  },
  tap: {
    scale: 0.95,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 25,
      duration: 0.1
    }
  }
};

const navigationItems = {
  authenticated: [
    { id: 'overview', label: 'Overview', icon: Home, path: '/dashboard' },
    { id: 'services', label: 'Services', icon: Settings, path: '/services' },
    { id: 'notifications', label: 'Notifications', icon: Bell, path: '/notifications' },
    { id: 'support', label: 'Support', icon: HelpCircle, path: '/support' }
  ],
  public: [
    { id: 'about', label: 'About Us', icon: Info, path: '/about' },
    { id: 'blog', label: 'Blog', icon: BookOpen, path: '/blog' },
    { id: 'case-studies', label: 'Case Studies', icon: FileText, path: '/case-studies' }
  ]
};

const menuItemVariants = {
  hidden: { x: -10, opacity: 0 },
  visible: {
    x: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 25,
      duration: 0.1
    }
  },
  hover: {
    x: 5,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 25,
      duration: 0.1
    }
  },
  tap: {
    scale: 0.98,
    transition: {
      duration: 0.05
    }
  }
};

const dividerVariants = {
  hidden: { scaleX: 0, opacity: 0 },
  visible: {
    scaleX: 1,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 25,
      duration: 0.1
    }
  }
};

export default function Navigation({ isSidebarOpen, setIsSidebarOpen, activeTab, setActiveTab }: NavigationProps) {
  const [scrolled, setScrolled] = useState(false);
  const [session, setSession] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        fetchUnreadCount();
        setupNotificationSubscription();
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        fetchUnreadCount();
        setupNotificationSubscription();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('read', false);

      if (error) throw error;
      setUnreadCount(count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const setupNotificationSubscription = () => {
    const channel = supabase
      .channel('notifications_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications'
        },
        () => {
          fetchUnreadCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setScrolled(window.scrollY > 20);
      }, 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location, setIsSidebarOpen]);

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const handleNavItemClick = (item: typeof navigationItems.authenticated[0]) => {
    if (item.path) {
      navigate(item.path);
    }
    setActiveTab?.(item.id);
    setIsSidebarOpen(false);
  };

  return (
    <>
      <motion.nav 
        variants={navVariants}
        initial="hidden"
        animate="visible"
        className={`fixed top-0 left-0 right-0 z-50 px-4 py-3 bg-white/80 backdrop-blur-lg ${
          scrolled ? 'shadow-md' : 'shadow-sm'
        } transition-shadow duration-200`}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <motion.div 
            className="flex items-center gap-2 group"
            variants={logoVariants}
            whileHover="hover"
            whileTap="tap"
          >
            <motion.button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white font-bold text-xl w-8 h-8 rounded-lg group-hover:bg-blue-700 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center"
              aria-label="Go to homepage"
            >
              SS
            </motion.button>
            <Link 
              to="/"
              className="hidden sm:block text-lg font-bold text-blue-600 group-hover:text-blue-700 transition-colors duration-150"
            >
              Soft Standards Inc.
            </Link>
          </motion.div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ duration: 0.1 }}
            onClick={() => setIsSidebarOpen(true)}
            className="bg-blue-600 text-white w-8 h-8 rounded-lg hover:bg-blue-700 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center justify-center"
            aria-label={isSidebarOpen ? "Close menu" : "Open menu"}
          >
            <Menu className="h-5 w-5" />
          </motion.button>
        </div>
      </motion.nav>

      {/* Mobile Sidebar */}
      <AnimatePresence mode="wait">
        {isSidebarOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
              onClick={() => setIsSidebarOpen(false)}
            />

            {/* Sidebar */}
            <motion.div
              variants={sidebarVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="fixed right-0 top-0 h-full w-full sm:w-80 bg-white shadow-xl z-50 overflow-y-auto"
            >
              <motion.div 
                variants={itemVariants}
                className="p-4 border-b border-gray-100"
              >
                <div className="flex items-center justify-between">
                  <motion.div 
                    className="flex items-center gap-2"
                    variants={logoVariants}
                    whileHover="hover"
                    whileTap="tap"
                  >
                    <div className="bg-blue-600 text-white font-bold text-xl w-8 h-8 rounded-lg flex items-center justify-center">
                      SS
                    </div>
                    <span className="font-semibold text-gray-900">
                      {session ? 'Dashboard' : 'Menu'}
                    </span>
                  </motion.div>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ duration: 0.1 }}
                    onClick={() => setIsSidebarOpen(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-150"
                    aria-label="Close menu"
                  >
                    <X className="w-5 h-5 text-gray-600" />
                  </motion.button>
                </div>
              </motion.div>

              <motion.nav 
                className="p-4"
                variants={{
                  visible: {
                    transition: {
                      staggerChildren: 0.02
                    }
                  }
                }}
              >
                {session ? (
                  // Authenticated navigation items
                  <>
                    {navigationItems.authenticated.map((item, index) => (
                      <motion.button
                        key={item.id}
                        variants={menuItemVariants}
                        whileHover="hover"
                        whileTap="tap"
                        onClick={() => handleNavItemClick(item)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-all duration-150 ${
                          activeTab === item.id
                            ? 'bg-blue-50 text-blue-600 shadow-sm'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                        {item.id === 'notifications' && unreadCount > 0 && (
                          <motion.span
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="ml-auto bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full text-xs font-medium"
                          >
                            {unreadCount}
                          </motion.span>
                        )}
                      </motion.button>
                    ))}
                    <motion.button
                      variants={menuItemVariants}
                      whileHover="hover"
                      whileTap="tap"
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-1 text-red-600 hover:bg-red-50 transition-all duration-150"
                    >
                      <LogOut className="w-5 h-5" />
                      <span className="font-medium">Logout</span>
                    </motion.button>
                  </>
                ) : (
                  // Public navigation items
                  <>
                    <motion.div variants={menuItemVariants}>
                      <Link
                        to="/login"
                        className="w-full flex items-center px-4 py-3 rounded-lg mb-4 text-blue-600 hover:bg-blue-50 transition-all duration-150 font-medium"
                        onClick={() => setIsSidebarOpen(false)}
                      >
                        Login
                      </Link>
                    </motion.div>

                    <motion.div
                      variants={dividerVariants}
                      className="h-px bg-gray-200 my-4 transform origin-left"
                    />

                    {navigationItems.public.map((item, index) => (
                      <motion.div
                        key={item.id}
                        variants={menuItemVariants}
                        whileHover="hover"
                        whileTap="tap"
                        className="mb-1"
                      >
                        <Link
                          to={item.path}
                          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-all duration-150"
                          onClick={() => setIsSidebarOpen(false)}
                        >
                          <item.icon className="w-5 h-5" />
                          <span className="font-medium">{item.label}</span>
                        </Link>
                      </motion.div>
                    ))}
                  </>
                )}
              </motion.nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}